import type { ReactNode } from 'react';
import { Keyboard, Pressable, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { YStack } from 'tamagui';

import { space } from '@/theme/tokens.generated';
import { useDockInset } from '@/lib/dock';

// This app is mobile-first (React Native), and screens were only ever
// verified at a phone-width viewport -- on a real desktop browser,
// unconstrained flex:1 content pins to the top-left of the window and
// full-width elements (buttons, dividers) stretch edge to edge across
// it. ScreenContainer gives every screen the same fix: an outer layer
// that fills the viewport with the page background, and an inner
// column capped at phone width and centered -- the standard treatment
// for a mobile app rendered on web (compare Twitter/Threads web).
//
// It's also the one place safe-area insets are applied, rather than
// each screen guessing at a fixed `pt="$8"` (the app previously had
// three different hardcoded approximations of "below the status bar" --
// wrong on a Dynamic Island, worse under RN 0.86's edge-to-edge Android
// default). `topInset` defaults to true for screens with no native
// header of their own (Preview, VerificationPending, and -- as of the
// whole-app critique 2026-09-02's duplicate-title fix -- sign-up,
// profile, and create-thread, which used to render a redundant native
// title above their own in-body heading, and -- as of the 2026-09-04
// "Thread" duplicate-title fix -- thread detail too); the thread list
// tab screen is the one exception still presented with a themed native
// header, already rendering below the notch via that header, so it
// alone passes `topInset={false}`.
// hex + alpha, for callers that need a real rgba value rather than a token
// string (scroll-edge fades, the dock tint).
export function hexToRgba(hex: string, alpha: number): string {
  const value = hex.replace('#', '');
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Issue #295: at desktop width the column below caps at 480 and centers; it
// widens moderately at >=768px (640) so the margin recovered is real without a
// full desktop redesign.
const WIDE_BREAKPOINT = 768;
const WIDE_MAX_WIDTH = 640;
const NARROW_MAX_WIDTH = 480;

export function ScreenContainer({
  children,
  topInset = true,
  scrollUnderDock = false,
}: {
  children: ReactNode;
  topInset?: boolean;
  // true: the screen's own scroll container pads by useDockInset() and the dock
  // floats over it; false: the dock's footprint stays reserved (lib/dock.ts).
  scrollUnderDock?: boolean;
}) {
  const dockInset = useDockInset();
  const insets = useSafeAreaInsets();
  const { width: windowWidth } = useWindowDimensions();
  const isWide = windowWidth >= WIDE_BREAKPOINT;

  return (
    <YStack flex={1} backgroundColor="$background" alignItems="center">
      {/* Ground: flat $background, no gradient or wash (decision 49,
          issue #627). Cards are opaque and carry their own edge; glass lives
          only on the dock and scrims. */}
      <YStack
        flex={1}
        width="100%"
        maxWidth={isWide ? WIDE_MAX_WIDTH : NARROW_MAX_WIDTH}
        paddingHorizontal={isWide ? '$6' : '$4'}
        paddingTop={(topInset ? insets.top : 0) + space[4]}
        paddingBottom={scrollUnderDock ? 0 : dockInset > 0 ? dockInset : insets.bottom}
      >
        {/* Real user-reported bug, 2026-09-03: the keyboard never
            dismissed on tapping outside a focused input, on every
            screen in the app. Keyboard.dismiss() correctly blurs the
            currently focused input on react-native-web (checked
            against node_modules/react-native-web's own
            dismissKeyboard source, not assumed), so this fix reaches
            the deployed web build too, not just native. Pressable
            wrapping the whole content area (not just a specific
            region) is safe here: RN's responder system lets a nested
            Pressable/Button/FlatList item capture its own touch
            without bubbling to this ancestor, so real taps on buttons,
            links, and list items still work exactly as before -- only
            a tap that lands on nothing interactive reaches this
            handler.

            accessible={false} is required, not optional -- caught in
            PR #65's own review: RN's Pressable defaults accessible to
            true (Pressable.js), which on iOS makes the view ONE
            accessibility element and hides every descendant from
            VoiceOver individually. Left at its default here, this
            single wrapper (used by EVERY screen in the app) would have
            collapsed each screen's entire content into one opaque
            VoiceOver node -- every button, input, and piece of text on
            the screen unreachable on its own. Web is unaffected
            (RNW's Pressable sets no such semantics), which is exactly
            why a Chromium-only render check didn't catch it. */}
        <Pressable accessible={false} onPress={() => Keyboard.dismiss()} style={{ flex: 1 }}>
          {children}
        </Pressable>
      </YStack>
    </YStack>
  );
}
